import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import {
  MessageSquare,
  Search,
  Filter,
  Calendar,
  User,
  Bot,
  TrendingUp,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  HelpCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ChatInteraction {
  id: string;
  conversation_id: string;
  user_id: string | null;
  session_id: string | null;
  user_message: string;
  bot_response: string;
  matched_chunks: number;
  knowledge_source_type: 'knowledge_base' | 'support_request' | 'fallback' | 'moderation_blocked' | 'triage_cat1' | 'triage_cat2' | 'triage_cat3';
  created_at: string;
}

interface ConversationGroup {
  conversation_id: string;
  interactions: ChatInteraction[];
  user_id: string | null;
  session_id: string | null;
  message_count: number;
  created_at: string;
  latest_at: string;
}

type FilterType = 'all' | 'knowledge_base' | 'support_request' | 'fallback' | 'moderation_blocked' | 'triage_cat1' | 'triage_cat2' | 'triage_cat3';

interface AdminChatbotLogsProps {
  noLayout?: boolean;
}

export function AdminChatbotLogs({ noLayout = false }: AdminChatbotLogsProps = {}) {
  const [conversations, setConversations] = useState<ConversationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [expandedConversations, setExpandedConversations] = useState<Set<string>>(new Set());
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [stats, setStats] = useState({
    total: 0,
    knowledge_base: 0,
    support_request: 0,
    fallback: 0,
    moderation_blocked: 0,
    triage_cat1: 0,
    triage_cat2: 0,
    triage_cat3: 0,
    unique_conversations: 0,
  });

  useEffect(() => {
    fetchLogs();
  }, [filterType, dateFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('chat_interactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterType !== 'all') {
        query = query.eq('knowledge_source_type', filterType);
      }

      const now = new Date();
      let startDate: Date | null = null;

      switch (dateFilter) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setDate(now.getDate() - 30));
          break;
      }

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      const interactions = data || [];

      const conversationMap = new Map<string, ConversationGroup>();
      interactions.forEach((interaction: ChatInteraction) => {
        const convId = interaction.conversation_id;
        if (!conversationMap.has(convId)) {
          conversationMap.set(convId, {
            conversation_id: convId,
            interactions: [],
            user_id: interaction.user_id,
            session_id: interaction.session_id,
            message_count: 0,
            created_at: interaction.created_at,
            latest_at: interaction.created_at,
          });
        }
        const group = conversationMap.get(convId)!;
        group.interactions.push(interaction);
        group.message_count++;
        if (new Date(interaction.created_at) > new Date(group.latest_at)) {
          group.latest_at = interaction.created_at;
        }
      });

      const sortedConversations = Array.from(conversationMap.values()).sort(
        (a, b) => new Date(b.latest_at).getTime() - new Date(a.latest_at).getTime()
      );

      setConversations(sortedConversations);

      setStats({
        total: interactions.length,
        knowledge_base: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'knowledge_base').length,
        support_request: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'support_request').length,
        fallback: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'fallback').length,
        moderation_blocked: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'moderation_blocked').length,
        triage_cat1: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'triage_cat1').length,
        triage_cat2: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'triage_cat2').length,
        triage_cat3: interactions.filter((i: ChatInteraction) => i.knowledge_source_type === 'triage_cat3').length,
        unique_conversations: conversationMap.size,
      });

    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleConversation = (conversationId: string) => {
    const newExpanded = new Set(expandedConversations);
    if (newExpanded.has(conversationId)) {
      newExpanded.delete(conversationId);
    } else {
      newExpanded.add(conversationId);
    }
    setExpandedConversations(newExpanded);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.interactions.some(int =>
      int.user_message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      int.bot_response.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const getSourceTypeIcon = (type: string) => {
    switch (type) {
      case 'knowledge_base':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'support_request':
        return <HelpCircle className="w-4 h-4 text-blue-600" />;
      case 'fallback':
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      case 'moderation_blocked':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'triage_cat1':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'triage_cat2':
        return <HelpCircle className="w-4 h-4 text-amber-600" />;
      case 'triage_cat3':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceTypeLabel = (type: string) => {
    switch (type) {
      case 'knowledge_base':
        return 'Kennisbank';
      case 'support_request':
        return 'Support verzoek';
      case 'fallback':
        return 'Geen resultaat';
      case 'moderation_blocked':
        return 'Geblokkeerd';
      case 'triage_cat1':
        return 'Cat 1: feitelijk (AI zelf)';
      case 'triage_cat2':
        return 'Cat 2: subjectief';
      case 'triage_cat3':
        return 'Cat 3: risicovol';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const content = (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chatbot Logs</h1>
          <p className="text-gray-600 mt-1">Bekijk alle chatbot interacties en conversaties</p>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">Totaal</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Cat 1: feitelijk (AI zelf)</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.triage_cat1}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-gray-600">Cat 2: subjectief</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats.triage_cat2}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Cat 3: risicovol</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.triage_cat3}</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Kennisbank</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.knowledge_base}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Support verzoek</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.support_request}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-gray-600">Geen resultaat</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats.fallback}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Geblokkeerd</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.moderation_blocked}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Zoek in conversaties..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="today">Vandaag</option>
                <option value="week">Laatste 7 dagen</option>
                <option value="month">Laatste 30 dagen</option>
                <option value="all">Alle tijd</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alles ({stats.total})
              </button>
              <button
                onClick={() => setFilterType('triage_cat1')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'triage_cat1'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cat 1 ({stats.triage_cat1})
              </button>
              <button
                onClick={() => setFilterType('triage_cat2')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'triage_cat2'
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cat 2 ({stats.triage_cat2})
              </button>
              <button
                onClick={() => setFilterType('triage_cat3')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'triage_cat3'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cat 3 ({stats.triage_cat3})
              </button>
              <button
                onClick={() => setFilterType('knowledge_base')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'knowledge_base'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Kennisbank ({stats.knowledge_base})
              </button>
              <button
                onClick={() => setFilterType('support_request')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'support_request'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Support ({stats.support_request})
              </button>
              <button
                onClick={() => setFilterType('fallback')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'fallback'
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Geen resultaat ({stats.fallback})
              </button>
              <button
                onClick={() => setFilterType('moderation_blocked')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterType === 'moderation_blocked'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Geblokkeerd ({stats.moderation_blocked})
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="text-gray-500 mt-2">Laden...</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Geen conversaties gevonden</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredConversations.map((conv) => {
                const isExpanded = expandedConversations.has(conv.conversation_id);
                const firstInteraction = conv.interactions[conv.interactions.length - 1];

                return (
                  <div key={conv.conversation_id} className="hover:bg-gray-50 transition-colors">
                    <div
                      onClick={() => toggleConversation(conv.conversation_id)}
                      className="p-4 cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500 font-mono">
                              {conv.conversation_id.substring(0, 8)}
                            </span>
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              {conv.message_count} berichten
                            </span>
                            {conv.user_id && (
                              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                Ingelogd
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-900 font-medium mb-1 line-clamp-1">
                            {firstInteraction.user_message}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(conv.created_at)} - {formatDate(conv.latest_at)}
                          </p>
                        </div>
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-4">
                        {conv.interactions.slice().reverse().map((interaction) => (
                          <div key={interaction.id} className="space-y-3 border-l-2 border-gray-200 pl-4">
                            <div className="bg-blue-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <User className="w-4 h-4 text-blue-600" />
                                <span className="text-xs font-medium text-blue-900">Gebruiker</span>
                                <span className="text-xs text-gray-500">{formatDate(interaction.created_at)}</span>
                              </div>
                              <p className="text-sm text-gray-900">{interaction.user_message}</p>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Bot className="w-4 h-4 text-gray-600" />
                                <span className="text-xs font-medium text-gray-900">dibbi</span>
                                {getSourceTypeIcon(interaction.knowledge_source_type)}
                                <span className="text-xs text-gray-500">
                                  {getSourceTypeLabel(interaction.knowledge_source_type)}
                                </span>
                                {interaction.matched_chunks > 0 && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                    {interaction.matched_chunks} chunks
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                {interaction.bot_response}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
  );

  if (noLayout) {
    return content;
  }

  return <AdminLayout>{content}</AdminLayout>;
}
